import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Users, TrendingUp, TrendingDown, Mail, MousePointerClick, Eye,
  FileSearch, ExternalLink, Info, Globe2, Smartphone, Radio, Target, Activity, Clock,
} from "lucide-react";

const fmt = (n) => (typeof n === "number" ? new Intl.NumberFormat("en-US").format(n) : n ?? "—");

// KPI tile with an optional gold accent + delta indicator.
const Tile = ({ label, value, sub, Icon, accent, testid, primary }) => (
  <div className={primary ? "fx-kpi-primary" : "ft-card"} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[var(--ft-muted)]">{label}</div>
      <Icon size={14} className={accent ? "text-[var(--ft-gold)]" : "text-[var(--ft-muted)]"} strokeWidth={1.5} />
    </div>
    <div className={`text-3xl font-bold tracking-tight ${accent ? "text-[var(--ft-gold)]" : "text-[var(--ft-black)]"}`}>
      {value ?? "—"}
    </div>
    {sub && <div className="text-xs text-[var(--ft-muted)] mt-1">{sub}</div>}
  </div>
);

// Two-column data table used across GA4 breakdown panels — top pages, traffic
// sources, geography, device. Renders a graceful empty state when the array
// is missing so the panel never collapses.
const DataPanel = ({ title, subtitle, rows, columns, emptyText, testid, Icon }) => (
  <div className="ft-card p-0 overflow-hidden" data-testid={testid}>
    <div className="px-5 py-4 border-b border-[var(--ft-border)] flex items-start justify-between gap-3">
      <div>
        <div className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[var(--ft-black)] flex items-center gap-2">
          {Icon && <Icon size={12} className="text-[var(--ft-gold)]" />}
          {title}
        </div>
        {subtitle && <div className="text-xs text-[var(--ft-muted)] mt-1">{subtitle}</div>}
      </div>
    </div>
    {rows && rows.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="ft-table fx-dense">
          <thead>
            <tr>{columns.map((c) => <th key={c.key} className={c.numeric ? "text-right" : ""}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`${testid}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className={c.numeric ? "text-right font-semibold tabular-nums" : ""}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="fx-empty" style={{ border: "none", padding: "2.5rem 1.5rem" }}>
        <div className="fx-empty-icon">{Icon ? <Icon size={20} /> : <Info size={20} />}</div>
        <h4>No data yet</h4>
        <p>{emptyText}</p>
      </div>
    )}
  </div>
);

const AdminAnalytics = () => {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)),
      api.get(`/admin/analytics/config`).then((r) => setCfg(r.data)),
    ]).then((res) => {
      if (res.some((r) => r.status === "rejected")) toast.error("Failed to load analytics");
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [days]);

  const internal = data?.providers?.internal?.data;
  const ga4Payload = data?.providers?.ga4;
  const ga4 = ga4Payload?.data;
  const ga4Live = ga4?.configured === true && !ga4?.error;
  const leadDelta = internal ? (internal.leads.current - internal.leads.previous) : 0;
  const deltaPct = internal && internal.leads.previous
    ? Math.round((leadDelta / internal.leads.previous) * 100)
    : (leadDelta > 0 ? 100 : 0);

  return (
    <AdminLayout>
      <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
        <div>
          <div className="ft-eyebrow mb-2 flex items-center gap-2">
            <BarChart3 size={13} className="text-[var(--ft-gold)]"/> Website Analytics
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">
            Executive dashboard
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            First-party funnel numbers, plus live GA4 traffic once your service-account credentials are in place.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-[var(--ft-border)] p-0.5 bg-white" data-testid="analytics-range">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-[0.65rem] font-semibold tracking-[0.14em] uppercase px-3 py-1.5 rounded-full transition-all ${
                days === d ? "bg-[var(--ft-black)] text-white" : "text-[var(--ft-muted)] hover:text-[var(--ft-black)]"
              }`}
              data-testid={`analytics-range-${d}`}
            >
              Last {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Provider status callouts */}
      <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="analytics-providers">
        {cfg && Object.entries(cfg.providers).map(([key, s]) => (
          <div key={key} className="ft-card flex items-start gap-3" data-testid={`provider-${key}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
              s.configured ? "bg-[var(--ft-gold-soft)] text-[var(--ft-gold)]" : "bg-[var(--ft-light-gray)] text-[var(--ft-muted)]"
            }`}>
              <Info size={14} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-[var(--ft-black)] uppercase tracking-[0.14em] text-xs">
                  {key === "ga4" ? "Google Analytics 4" : "First-Party (Mongo)"}
                </div>
                <span className={`text-[0.55rem] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                  s.configured ? "bg-[var(--ft-gold)] text-[var(--ft-black)]" : "bg-[var(--ft-charcoal)] text-white/80"
                }`}>
                  {s.configured ? "Connected" : "Not connected"}
                </span>
              </div>
              {s.note && <div className="text-xs text-[var(--ft-muted)] mt-1 leading-relaxed">{s.note}</div>}
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="grid md:grid-cols-4 gap-4">
          {[0,1,2,3].map(i => (
            <div key={i} className="ft-card">
              <div className="fx-skeleton fx-skeleton-line w-20 mb-3" />
              <div className="fx-skeleton fx-skeleton-title" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Primary KPIs — first-party funnel signal */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4" data-testid="analytics-tiles">
            <Tile primary testid="tile-leads" label="Inquiries"
              value={fmt(internal?.leads?.current ?? 0)}
              sub={<span className={leadDelta >= 0 ? "text-[var(--ft-gold)]" : "text-red-600"}>
                {leadDelta >= 0 ? <TrendingUp size={10} className="inline mb-0.5"/> : <TrendingDown size={10} className="inline mb-0.5"/>}
                {" "}{deltaPct >= 0 ? "+" : ""}{deltaPct}% vs prior {days}d
              </span>}
              Icon={Users} accent />
            <Tile primary testid="tile-samples" label="Sample Requests"
              value={fmt(internal?.samples ?? 0)}
              sub={`over last ${days} days`} Icon={FileSearch} accent />
            <Tile testid="tile-open-rate" label="Email Open Rate"
              value={`${internal?.email?.open_rate ?? 0}%`}
              sub={`${internal?.email?.opened ?? 0} of ${internal?.email?.delivered ?? 0} delivered`}
              Icon={Eye} />
            <Tile testid="tile-click-rate" label="Click Rate"
              value={`${internal?.email?.click_rate ?? 0}%`}
              sub={`${internal?.email?.clicked ?? 0} clicks`}
              Icon={MousePointerClick} />
          </div>

          {/* GA4 — Traffic + engagement */}
          {ga4Live ? (
            <>
              <div className="ft-eyebrow mb-3 mt-8 flex items-center gap-2">
                <Radio size={12} className="text-[var(--ft-gold)]" />
                Live Google Analytics 4
                <span className="text-[0.55rem] text-[var(--ft-muted)] normal-case tracking-normal font-normal ml-1">
                  Property {ga4Payload?.status?.property_id} · refreshed every 5 min
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="ga4-kpis">
                <Tile testid="ga4-active-users" label="Active Users" value={fmt(ga4.summary?.active_users)} Icon={Users} accent />
                <Tile testid="ga4-sessions" label="Sessions" value={fmt(ga4.summary?.sessions)} Icon={Activity} />
                <Tile testid="ga4-page-views" label="Page Views" value={fmt(ga4.summary?.page_views)} Icon={Eye} />
                <Tile testid="ga4-engagement" label="Engagement Rate" value={`${ga4.summary?.engagement_rate ?? 0}%`} Icon={Target} />
                <Tile testid="ga4-session-len" label="Avg Session"
                  value={`${Math.round(ga4.summary?.avg_session_seconds ?? 0)}s`}
                  Icon={Clock} />
              </div>

              <div className="grid lg:grid-cols-2 gap-4 mb-6">
                <DataPanel
                  testid="ga4-top-pages" title="Top Pages" Icon={FileSearch}
                  subtitle="Ranked by pageviews across the site"
                  rows={ga4.top_pages}
                  columns={[
                    { key: "path", label: "Path", render: (r) => <span className="text-[var(--ft-charcoal)]">{r.path}</span> },
                    { key: "views", label: "Views", numeric: true, render: (r) => fmt(r.views) },
                    { key: "users", label: "Users", numeric: true, render: (r) => fmt(r.users) },
                  ]}
                  emptyText="No pageviews yet in this range."
                />
                <DataPanel
                  testid="ga4-traffic" title="Traffic Sources" Icon={Radio}
                  subtitle="Where sessions originated"
                  rows={ga4.traffic_sources}
                  columns={[
                    { key: "channel", label: "Channel", render: (r) => <span className="font-semibold">{r.channel}</span> },
                    { key: "sessions", label: "Sessions", numeric: true, render: (r) => fmt(r.sessions) },
                    { key: "users", label: "Users", numeric: true, render: (r) => fmt(r.users) },
                  ]}
                  emptyText="No traffic recorded yet — check back after real users visit."
                />
              </div>

              <div className="grid lg:grid-cols-2 gap-4 mb-6">
                <DataPanel
                  testid="ga4-geo" title="Geography" Icon={Globe2}
                  subtitle="Top countries by active users"
                  rows={ga4.geography?.slice(0, 10)}
                  columns={[
                    { key: "country", label: "Country" },
                    { key: "users", label: "Users", numeric: true, render: (r) => fmt(r.users) },
                    { key: "sessions", label: "Sessions", numeric: true, render: (r) => fmt(r.sessions) },
                  ]}
                  emptyText="Geographic data will appear once GA4 accumulates traffic."
                />
                <DataPanel
                  testid="ga4-device" title="Device Category" Icon={Smartphone}
                  subtitle="How users reach the site"
                  rows={ga4.device_category}
                  columns={[
                    { key: "device", label: "Device", render: (r) => <span className="font-semibold capitalize">{r.device}</span> },
                    { key: "sessions", label: "Sessions", numeric: true, render: (r) => fmt(r.sessions) },
                    { key: "users", label: "Users", numeric: true, render: (r) => fmt(r.users) },
                  ]}
                  emptyText="Device breakdown will appear soon."
                />
              </div>

              <div className="mb-6">
                <DataPanel
                  testid="ga4-events" title="Conversion Events" Icon={Target}
                  subtitle="Counts for each tracked conversion (contact, sample, consult, product view, portfolio view, kit, download)"
                  rows={ga4.events}
                  columns={[
                    { key: "event_name", label: "Event",
                      render: (r) => <span className="font-mono text-xs bg-[var(--ft-light-gray)] px-2 py-0.5 rounded text-[var(--ft-charcoal)]">{r.event_name}</span> },
                    { key: "count", label: "Count", numeric: true, render: (r) => fmt(r.count) },
                  ]}
                  emptyText="No conversions in this range yet. As traffic flows in, contact_submit / sample_request_submit / consult_booked / product_view / portfolio_view / kit_selected / case_study_download counts land here."
                />
              </div>
            </>
          ) : (
            /* GA4 not configured — first-party fallback + activation guide */
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="fallback-tiles">
                <Tile testid="tile-contacts" label="Contacts" value={fmt(internal?.leads?.contacts ?? 0)} Icon={Mail} />
                <Tile testid="tile-consults" label="Consultations" value={fmt(internal?.leads?.consultations ?? 0)} Icon={Users} />
                <Tile testid="tile-emails-sent" label="Emails Sent" value={fmt(internal?.email?.sent ?? 0)} Icon={Mail} />
                <Tile testid="tile-emails-clicked" label="Emails Clicked" value={fmt(internal?.email?.clicked ?? 0)} Icon={MousePointerClick} />
              </div>

              {internal?.top_pages?.length > 0 && (
                <div className="mb-6">
                  <DataPanel
                    testid="internal-top-pages" title="Top Portfolio Views (First-Party)" Icon={FileSearch}
                    subtitle="Portfolio views tracked in our own database — GA4 will replace this with site-wide data once connected"
                    rows={internal.top_pages}
                    columns={[
                      { key: "title", label: "Brand", render: (r) => <span className="font-semibold">{r.title}</span> },
                      { key: "path", label: "Path", render: (r) => <span className="text-xs text-[var(--ft-muted)]">{r.path}</span> },
                      { key: "views", label: "Views", numeric: true, render: (r) => fmt(r.views) },
                      { key: "conversions", label: "CTA Clicks", numeric: true, render: (r) => <span className="text-[var(--ft-gold)]">{fmt(r.conversions)}</span> },
                    ]}
                    emptyText="Portfolio views will appear here as visitors explore case studies."
                  />
                </div>
              )}
            </>
          )}

          {/* SEO surfaces card + GA4 activation guide */}
          {cfg && (
            <div className="ft-card" data-testid="analytics-seo">
              <div className="ft-eyebrow mb-3">Search Console & GA4 Setup</div>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <a href={cfg.sitemap_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-lg border border-[var(--ft-border)] hover:border-[var(--ft-gold)] transition-all fx-tap"
                  data-testid="analytics-sitemap-link">
                  <ExternalLink size={13} className="text-[var(--ft-gold)]"/>
                  <div className="flex-1">
                    <div className="font-semibold">Sitemap</div>
                    <div className="text-xs text-[var(--ft-muted)] truncate">{cfg.sitemap_url}</div>
                  </div>
                </a>
                <a href={cfg.robots_url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 rounded-lg border border-[var(--ft-border)] hover:border-[var(--ft-gold)] transition-all fx-tap"
                  data-testid="analytics-robots-link">
                  <ExternalLink size={13} className="text-[var(--ft-gold)]"/>
                  <div className="flex-1">
                    <div className="font-semibold">robots.txt</div>
                    <div className="text-xs text-[var(--ft-muted)] truncate">{cfg.robots_url}</div>
                  </div>
                </a>
              </div>
              {!ga4Live && (
                <div className="text-xs text-[var(--ft-muted)] mt-3 leading-relaxed border-t border-[var(--ft-border)] pt-3">
                  <strong className="text-[var(--ft-charcoal)]">To activate live GA4 data:</strong>{" "}
                  In Google Cloud Console: (1) create/select a project, enable <em>Google Analytics Data API v1</em>,
                  (2) create a Service Account, generate a JSON key, (3) in GA4 → Admin → Property Access Management,
                  add the service-account email as a <em>Viewer</em>, (4) paste the full JSON into{" "}
                  <code className="bg-[var(--ft-light-gray)] px-1 py-0.5 rounded">GA4_SERVICE_ACCOUNT_JSON</code> and the numeric Property ID into{" "}
                  <code className="bg-[var(--ft-light-gray)] px-1 py-0.5 rounded">GA4_PROPERTY_ID</code> in backend/.env, then restart backend.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminAnalytics;
